import React, { useEffect, useState } from "react";
import { api, formatDate } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Trash2, Search, Upload, Download } from "lucide-react";
import { toast } from "sonner";
import ImportDialog from "../components/ImportDialog";
import { downloadCsv } from "../lib/download";

const emptyForm = { full_name:"", company:"", job_title:"", phone:"", whatsapp:"", email:"", address:"", industry:"", category:"prospect", lead_source:"", notes:"" };

export default function Contacts() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [industries, setIndustries] = useState([]);
  const [sources, setSources] = useState([]);

  const load = async () => {
    const r = await api.get("/contacts", { params: { q, category } });
    setItems(r.data);
  };
  useEffect(() => { load(); }, [q, category]);

  // Refetch settings on every dialog open so Settings changes are always reflected
  useEffect(() => {
    if (!open) return;
    api.get("/settings/industries").then(r => setIndustries(r.data));
    api.get("/settings/sources").then(r => setSources(r.data));
  }, [open]);

  const save = async () => {
    try {
      await api.post("/contacts", form);
      toast.success("Kontak ditambahkan");
      setOpen(false); setForm(emptyForm); load();
    } catch (e) { toast.error("Gagal menyimpan"); }
  };
  const del = async (id) => {
    if (!window.confirm("Hapus kontak ini?")) return;
    await api.delete(`/contacts/${id}`);
    toast.success("Kontak dihapus"); load();
  };
  const exportCsv = async () => {
    const ok = await downloadCsv(`/export/contacts.csv${category && category!=="all" ? `?category=${category}` : ""}`, `contacts-${new Date().toISOString().slice(0,10)}.csv`);
    if (ok) toast.success("Ekspor kontak berhasil"); else toast.error("Gagal ekspor");
  };

  return (
    <div className="space-y-5" data-testid="contacts-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold" style={{fontFamily:"Outfit"}}>Kontak</h1>
          <p className="text-sm text-slate-500">Basis data kontak, perusahaan, dan pelanggan.</p>
        </div>
        <div className="flex gap-2">
        <Button variant="outline" onClick={exportCsv} data-testid="export-contacts-btn"><Download className="w-4 h-4 mr-1"/>Export CSV</Button>
        <Button variant="outline" onClick={()=>setImportOpen(true)} data-testid="import-contacts-btn"><Upload className="w-4 h-4 mr-1"/>Import CSV/Excel</Button>
        <ImportDialog open={importOpen} onOpenChange={setImportOpen}
          endpoint="/contacts/import" templateUrl="/api/templates/contacts.csv"
          entityLabel="kontak" testIdPrefix="import-contacts"
          onDone={load}/>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="brand-btn" data-testid="add-contact-btn"><Plus className="w-4 h-4 mr-1"/>Tambah Kontak</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Kontak Baru</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3 py-2">
              <div className="col-span-2"><Label>Nama Lengkap</Label><Input data-testid="contact-name" value={form.full_name} onChange={(e)=>setForm({...form,full_name:e.target.value})}/></div>
              <div><Label>Perusahaan</Label><Input value={form.company} onChange={(e)=>setForm({...form,company:e.target.value})}/></div>
              <div><Label>Jabatan</Label><Input value={form.job_title} onChange={(e)=>setForm({...form,job_title:e.target.value})}/></div>
              <div><Label>Telepon</Label><Input value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})}/></div>
              <div><Label>WhatsApp</Label><Input value={form.whatsapp} onChange={(e)=>setForm({...form,whatsapp:e.target.value})}/></div>
              <div className="col-span-2"><Label>Email</Label><Input value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})}/></div>
              <div className="col-span-2"><Label>Alamat</Label><Input value={form.address} onChange={(e)=>setForm({...form,address:e.target.value})}/></div>
              <div>
                <Label>Industri</Label>
                <Select value={form.industry || undefined} onValueChange={(v)=>setForm({...form,industry:v})}>
                  <SelectTrigger data-testid="contact-industry"><SelectValue placeholder="Pilih industri"/></SelectTrigger>
                  <SelectContent>{industries.map(i=><SelectItem key={i.id} value={i.name}>{i.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Sumber</Label>
                <Select value={form.lead_source || undefined} onValueChange={(v)=>setForm({...form,lead_source:v})}>
                  <SelectTrigger data-testid="contact-source"><SelectValue placeholder="Pilih sumber"/></SelectTrigger>
                  <SelectContent>{sources.map(s=><SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Catatan</Label><Input value={form.notes} onChange={(e)=>setForm({...form,notes:e.target.value})}/></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={()=>setOpen(false)}>Batal</Button>
              <Button className="brand-btn" onClick={save} data-testid="contact-save">Simpan</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Card className="p-4">
        <div className="flex flex-col md:flex-row md:items-center gap-3 mb-3">
          <Tabs value={category} onValueChange={setCategory}>
            <TabsList>
              <TabsTrigger value="all" data-testid="tab-all">Semua</TabsTrigger>
              <TabsTrigger value="prospect" data-testid="tab-prospect">Prospek</TabsTrigger>
              <TabsTrigger value="customer" data-testid="tab-customer">Pelanggan</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="relative flex-1 max-w-sm">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Cari nama, perusahaan..." value={q} onChange={(e)=>setQ(e.target.value)} className="pl-9" data-testid="contacts-search"/>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 uppercase border-b">
              <tr>
                <th className="text-left py-2 px-2">Nama</th>
                <th className="text-left">Perusahaan</th>
                <th className="text-left">Jabatan</th>
                <th className="text-left">Telepon</th>
                <th className="text-left">Email</th>
                <th className="text-left">Kategori</th>
                <th className="text-left">Dibuat</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((c)=>(
                <tr key={c.id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`contact-row-${c.id}`}>
                  <td className="py-2 px-2 font-medium text-slate-800">{c.full_name}</td>
                  <td>{c.company}</td>
                  <td className="text-slate-600">{c.job_title}</td>
                  <td className="text-slate-600">{c.phone}</td>
                  <td className="text-slate-600">{c.email}</td>
                  <td><Badge variant="outline" className="capitalize">{c.category}</Badge></td>
                  <td className="text-slate-500 text-xs">{formatDate(c.created_at)}</td>
                  <td className="text-right pr-2"><button onClick={()=>del(c.id)} className="text-rose-500 hover:text-rose-700" data-testid={`del-contact-${c.id}`}><Trash2 className="w-4 h-4"/></button></td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-slate-500">Belum ada kontak.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
